import type { ApolloError, ServerError } from '@apollo/client';

import { isUploadError } from 'src/lib/errors';

interface ErrorInfo {
  title?: string;
  description?: string;
}

export const parseError = (error?: Error): ErrorInfo => {
  if (!error) {
    return { title: '' };
  }

  const apolloError = error as ApolloError;
  if (apolloError.networkError) {
    return {
      title: 'Network error',
      description: getNetworkErrorResult(apolloError.networkError),
    };
  }
  if (apolloError.graphQLErrors && apolloError.graphQLErrors.length > 0) {
    return {
      description: apolloError.graphQLErrors.map((gqlError) => gqlError.message).join('\n'),
    };
  }

  if (isUploadError(error)) {
    return {
      title: 'Processing error',
      description: error.message,
    };
  }

  return { title: error.message };
};

const getNetworkErrorResult = (error: Error): string => {
  if (error.name !== 'ServerError') {
    return error.message;
  }

  const serverError = error as ServerError;
  if (serverError.result && typeof serverError.result === 'object') {
    return serverError.result.error;
  }
  return serverError.result as string;
};

export const promisedWait = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(() => resolve(), ms);
  });

/**
 * Parse JSON string, return defaultValue if failed
 */
export const parseJson = <T = unknown>(jsonString?: string, defaultValue?: T): T | undefined => {
  if (!jsonString) {
    return defaultValue;
  }

  try {
    return JSON.parse(jsonString);
  } catch {
    return defaultValue;
  }
};

/**
 * Stringify passed value if it is present, otherwise return undefined
 */
export const stringifyIfPresent = (value: unknown): string | undefined => {
  if (value == null) {
    return undefined;
  }
  if (typeof value === 'string') {
    return value;
  }

  return JSON.stringify(value);
};
